/**
 * Controlled vocabularies for the Madar domain.
 *
 * Every one of these is a CHECK constraint in D1 as well as a Zod enum here.
 * The database is the authority; these exist so TypeScript and the UI agree
 * with it. Adding a value means a migration, deliberately — silent vocabulary
 * drift is how the legacy `status` column ended up meaning five different
 * things depending on `kind`.
 */

// ── Evidence and review ──────────────────────────────────────────────────────

/**
 * How strongly a fact is evidenced. Carried over from the legacy app, which got
 * this right and enforces it better than most commercial products.
 */
export const EVIDENCE_CLASSES = [
  'actual_document_verified', // read off a signed/official document
  'official_system_export', // Ejar, ZATCA, bank, Odoo export
  'user_report', // a person told us
  'prior_assistant_summary', // derived from an earlier summary, not the source
  'inferred', // calculated or assumed; never sufficient for approval
] as const;
export type EvidenceClass = (typeof EVIDENCE_CLASSES)[number];

/** Evidence classes strong enough to support `approved`. */
export const APPROVAL_GRADE_EVIDENCE: readonly EvidenceClass[] = [
  'actual_document_verified',
  'official_system_export',
];

export const REVIEW_STATES = ['baseline', 'reported', 'verified', 'approved', 'conflict'] as const;
export type ReviewState = (typeof REVIEW_STATES)[number];

/** Legal transitions. Anything not listed is rejected at the service layer. */
export const REVIEW_TRANSITIONS: Record<ReviewState, readonly ReviewState[]> = {
  baseline: ['reported', 'verified', 'conflict'],
  reported: ['verified', 'conflict'],
  verified: ['approved', 'reported', 'conflict'],
  approved: ['conflict'], // an approved figure can only be re-opened as a conflict
  conflict: ['reported', 'verified'],
};

export const SOURCE_KINDS = [
  'document',
  'system',
  'chat',
  'email',
  'manual',
  'site_visit',
] as const;
export type SourceKind = (typeof SOURCE_KINDS)[number];

// ── Roles and authorisation ──────────────────────────────────────────────────

/**
 * Roles are not a generic permission framework. They gate a short list of
 * chokepoints (see PERMISSIONS). Enforced server-side when building state —
 * hiding a button in the UI is theatre.
 */
export const ROLES = ['owner', 'manager', 'accountant', 'operator', 'legal', 'viewer'] as const;
export type Role = (typeof ROLES)[number];

export const PERMISSIONS = [
  'record:read',
  'record:write',
  'record:delete',
  'review:verify',
  'review:approve', // segregation of duties: cannot approve own entry
  'payment:record',
  'payment:allocate',
  'lease:write',
  'legal:read', // legal cases are restricted by default
  'legal:write',
  'expense:write',
  'report:export',
  'document:upload',
  'document:read',
  'admin:users',
  'admin:rules',
  'admin:backup',
  'admin:import',
  'audit:read',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const ALL: readonly Permission[] = PERMISSIONS;

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  owner: ALL,
  manager: [
    'record:read',
    'record:write',
    'review:verify',
    'review:approve',
    'payment:allocate',
    'lease:write',
    'legal:read',
    'expense:write',
    'report:export',
    'document:upload',
    'document:read',
    'admin:rules',
    'audit:read',
  ],
  accountant: [
    'record:read',
    'record:write',
    'review:verify',
    'payment:record',
    'payment:allocate',
    'expense:write',
    'report:export',
    'document:upload',
    'document:read',
    'audit:read',
  ],
  operator: ['record:read', 'record:write', 'review:verify', 'document:upload', 'document:read'],
  legal: [
    'record:read',
    'legal:read',
    'legal:write',
    'document:upload',
    'document:read',
    'report:export',
  ],
  viewer: ['record:read', 'report:export', 'document:read'],
};

export function can(roles: readonly Role[], permission: Permission): boolean {
  return roles.some((r) => ROLE_PERMISSIONS[r]?.includes(permission));
}

// ── Property, unit and measurement ───────────────────────────────────────────

export const PROPERTY_TYPES = [
  'retail',
  'office',
  'residential',
  'mixed',
  'land',
  'warehouse',
] as const;
export type PropertyType = (typeof PROPERTY_TYPES)[number];

export const UNIT_CATEGORIES = [
  'shop',
  'showroom',
  'office',
  'apartment',
  'kiosk',
  'warehouse',
  'parking',
  'storage',
  'other',
] as const;
export type UnitCategory = (typeof UNIT_CATEGORIES)[number];

/**
 * Occupancy is a normalised lifecycle, not a free-text label. `leased_future_start`
 * is the state the Bateel/Sandwich House lease is in: signed, rent not yet running.
 * Physical vs economic occupancy are derived from this, not stored.
 */
export const OCCUPANCY_STATUSES = [
  'vacant',
  'leased',
  'leased_future_start',
  'under_fitout',
  'under_renovation',
  'owner_occupied',
  'not_lettable',
  'unknown',
] as const;
export type OccupancyStatus = (typeof OCCUPANCY_STATUSES)[number];

/** Occupancy states that count as occupied for PHYSICAL occupancy. */
export const PHYSICALLY_OCCUPIED: readonly OccupancyStatus[] = [
  'leased',
  'under_fitout',
  'owner_occupied',
];
/** Occupancy states that count as occupied for ECONOMIC occupancy (rent running). */
export const ECONOMICALLY_OCCUPIED: readonly OccupancyStatus[] = ['leased'];

/**
 * Measurement standard an area figure follows. The Odoo import surfaced an
 * unresolved conflict between «مساحة السجاد (قدم²)» (carpet area, ft²) and
 * `sq_m`, and units carry `net_lettable_area_approved: false`.
 * RULE: no area whose standard is `unknown` or whose `approved_by` is null may
 * be used for pricing. Enforced in the pricing service, not just documented.
 */
export const AREA_STANDARDS = [
  'IPMS_1',
  'IPMS_2',
  'IPMS_3A',
  'IPMS_3B',
  'IPMS_3C', // RICS IPMS
  'BOMA',
  'GLA',
  'NLA',
  'GFA',
  'carpet',
  'title_deed',
  'unknown',
] as const;
export type AreaStandard = (typeof AREA_STANDARDS)[number];

export const AREA_UNITS = ['sqm', 'sqft'] as const;
export type AreaUnit = (typeof AREA_UNITS)[number];

export const SQFT_PER_SQM = 10.763910416709722;

// ── Parties ──────────────────────────────────────────────────────────────────

export const PARTY_TYPES = [
  'tenant',
  'vendor',
  'owner',
  'counterparty',
  'broker',
  'authority',
] as const;
export type PartyType = (typeof PARTY_TYPES)[number];

// ── Prospects ────────────────────────────────────────────────────────────────

/** Where a lead stands. The first four are open; `won`/`lost` close it. */
export const PROSPECT_STAGES = [
  'new',
  'contacted',
  'viewing',
  'negotiating',
  'won',
  'lost',
] as const;
export type ProspectStage = (typeof PROSPECT_STAGES)[number];
export const PROSPECT_OPEN_STAGES: readonly ProspectStage[] = [
  'new',
  'contacted',
  'viewing',
  'negotiating',
];

/** How a lead reached the office. `research` is a market study's proposal. */
export const PROSPECT_SOURCES = [
  'call',
  'visit',
  'broker',
  'referral',
  'advert',
  'research',
  'other',
] as const;
export type ProspectSource = (typeof PROSPECT_SOURCES)[number];

export const PROSPECT_TOUCH_KINDS = [
  'call',
  'visit',
  'meeting',
  'email',
  'message',
  'note',
] as const;
export type ProspectTouchKind = (typeof PROSPECT_TOUCH_KINDS)[number];

export const PARTY_FORMS = [
  'company',
  'establishment',
  'individual',
  'government',
  'unknown',
] as const;
export type PartyForm = (typeof PARTY_FORMS)[number];

/** Saudi registration identifiers. Stored raw, matched exactly. */
export const REGISTRATION_TYPES = [
  'cr', // السجل التجاري — 10 digits
  'vat', // الرقم الضريبي — 15 digits, starts and ends with 3
  'national_id', // هوية وطنية — 10 digits, starts with 1
  'iqama', // إقامة — 10 digits, starts with 2
  'unified_number', // الرقم الموحد — 7 digits, starts with 7
  'municipal_licence', // رخصة بلدية
  'rega_licence', // رخصة الهيئة العامة للعقار (فال)
  'other',
] as const;
export type RegistrationType = (typeof REGISTRATION_TYPES)[number];

// ── Leases ───────────────────────────────────────────────────────────────────

export const LEASE_STATUSES = [
  'draft',
  'pending_signature',
  'approved_future',
  'active',
  'holding_over',
  'expired',
  'terminated',
  'cancelled',
] as const;
export type LeaseStatus = (typeof LEASE_STATUSES)[number];

/** Statuses whose rent contributes to the rent roll and to WALE. */
export const RENT_ROLL_STATUSES: readonly LeaseStatus[] = ['active', 'holding_over'];

/** Ejar / REGA registration state. Confirmed 2026-09-16 as a real control. */
export const EJAR_STATUSES = [
  'registered',
  'pending',
  'not_registered',
  'expired',
  'not_required',
] as const;
export type EjarStatus = (typeof EJAR_STATUSES)[number];

export const PAYMENT_FREQUENCIES = [
  'monthly',
  'quarterly',
  'four_monthly',
  'semi_annual',
  'annual',
  'upfront',
  'custom',
] as const;
export type PaymentFrequency = (typeof PAYMENT_FREQUENCIES)[number];

export const MONTHS_PER_FREQUENCY: Record<PaymentFrequency, number | null> = {
  monthly: 1,
  quarterly: 3,
  four_monthly: 4,
  semi_annual: 6,
  annual: 12,
  upfront: null,
  custom: null,
};

/** Arabic labels the legacy data uses, mapped to the normalised vocabulary. */
export const ARABIC_FREQUENCY_MAP: Record<string, PaymentFrequency> = {
  شهري: 'monthly',
  'كل ثلاثة أشهر': 'quarterly',
  'ربع سنوي': 'quarterly',
  'كل أربعة أشهر': 'four_monthly',
  'كل ستة أشهر': 'semi_annual',
  'نصف سنوي': 'semi_annual',
  سنوي: 'annual',
  سنويا: 'annual',
  سنوياً: 'annual',
  'دفعة واحدة': 'upfront',
};

export const CHARGE_TYPES = [
  'base_rent',
  'service_charge',
  'vat',
  'electricity',
  'water',
  'cooling',
  'insurance',
  'marketing_levy',
  'turnover_rent',
  'parking',
  'penalty',
  'admin_fee',
  'ejar_fee',
  'other',
] as const;
export type ChargeType = (typeof CHARGE_TYPES)[number];

/** How rent steps up over the term. */
export const ESCALATION_TYPES = [
  'none',
  'fixed_percent',
  'fixed_amount',
  'cpi',
  'market_review',
  'stepped',
] as const;
export type EscalationType = (typeof ESCALATION_TYPES)[number];

/** Options a tenant or landlord may hold. Drive the critical-date engine. */
export const OPTION_TYPES = [
  'renewal',
  'break',
  'expansion',
  'termination',
  'purchase',
  'right_of_first_refusal',
] as const;
export type OptionType = (typeof OPTION_TYPES)[number];

export const OPTION_HOLDERS = ['tenant', 'landlord', 'mutual'] as const;
export type OptionHolder = (typeof OPTION_HOLDERS)[number];

export const OPTION_STATUSES = ['open', 'exercised', 'lapsed', 'waived'] as const;
export type OptionStatus = (typeof OPTION_STATUSES)[number];

/** Security deposit (التأمين) is a LIABILITY, not income. Tracked to refund. */
export const DEPOSIT_STATUSES = [
  'held',
  'partially_refunded',
  'refunded',
  'forfeited',
  'applied_to_arrears',
] as const;
export type DepositStatus = (typeof DEPOSIT_STATUSES)[number];

// ── Ledger ───────────────────────────────────────────────────────────────────

/**
 * The historical/expected split is load-bearing and inherited from the legacy
 * app: `historical` is the 1,852,263.90 SAR opening arrears baseline adopted on
 * 2026-09-07; `expected` is rent that will fall due under a live lease. They
 * must never be summed into one arrears figure, and a payment already reflected
 * in the baseline may not be allocated against a historical due a second time.
 */
export const LEDGER_TYPES = ['historical', 'expected'] as const;
export type LedgerType = (typeof LEDGER_TYPES)[number];

export const CHARGE_STATUSES = ['open', 'settled', 'written_off', 'disputed', 'void'] as const;
export type ChargeStatus = (typeof CHARGE_STATUSES)[number];

export const PAYMENT_METHODS = [
  'bank_transfer',
  'cheque',
  'cash',
  'pos',
  'sadad',
  'offset',
  'unknown',
] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_STATUSES = ['pending', 'cleared', 'bounced', 'void'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

/** Standard arrears ageing buckets. Every lender and valuer asks for these. */
export const AGEING_BUCKETS = ['current', 'd1_30', 'd31_60', 'd61_90', 'd90_plus'] as const;
export type AgeingBucket = (typeof AGEING_BUCKETS)[number];

export function ageingBucket(daysOverdue: number): AgeingBucket {
  if (daysOverdue <= 0) return 'current';
  if (daysOverdue <= 30) return 'd1_30';
  if (daysOverdue <= 60) return 'd31_60';
  if (daysOverdue <= 90) return 'd61_90';
  return 'd90_plus';
}

// ── Operations, legal, documents ─────────────────────────────────────────────

export const TASK_STATUSES = [
  'open',
  'progress',
  'waiting',
  'blocked',
  'closed',
  'cancelled',
] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type Priority = (typeof PRIORITIES)[number];

export const TASK_TYPES = [
  'task',
  'maintenance',
  'permit',
  'inspection',
  'collection',
  'legal_action',
] as const;
export type TaskType = (typeof TASK_TYPES)[number];

/**
 * Outcome of converting a Hijri deadline printed in the lawyer's report to a
 * Gregorian date. `unverified` means nobody has checked the conversion — a
 * real state in the 2026-08-30 report, and distinct from `ambiguous` (checked,
 * and genuinely could be two dates).
 */
export const HIJRI_CONVERSION_STATUSES = [
  'converted',
  'unverified',
  'ambiguous',
  'unconvertible',
  'not_applicable',
] as const;
export type HijriConversionStatus = (typeof HIJRI_CONVERSION_STATUSES)[number];

export const CASE_STAGES = [
  'pre_litigation',
  'filed',
  'first_instance',
  'appeal',
  'supreme',
  'execution',
  'settled',
  'closed',
  'withdrawn',
] as const;
export type CaseStage = (typeof CASE_STAGES)[number];

export const DOCUMENT_CATEGORIES = [
  'lease',
  'ejar_certificate',
  'title_deed',
  'invoice',
  'receipt',
  'bank_statement',
  'legal_filing',
  'legal_report',
  'valuation',
  'survey',
  'correspondence',
  'photo',
  'proposal',
  'quotation',
  'insurance',
  'municipal',
  'other',
] as const;
export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number];

/** Upload allowlist. Anything else is refused at the boundary — no exceptions. */
export const ALLOWED_MIME_TYPES: Record<string, string[]> = {
  'application/pdf': ['pdf'],
  'image/jpeg': ['jpg', 'jpeg'],
  'image/png': ['png'],
  'image/webp': ['webp'],
  'image/heic': ['heic'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['xlsx'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['docx'],
  'application/vnd.ms-excel': ['xls'],
  'text/csv': ['csv'],
  'message/rfc822': ['eml'],
};

/** 25 MB. The largest existing attachment is a 10.9 MB PDF (THOLAL REPORT). */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export const CURRENCY = 'SAR';
export const TIMEZONE = 'Asia/Riyadh';

// ── Data review (مراجعة البيانات) ────────────────────────────────────────────

/**
 * The legacy app kept 21 rows that are NOT tasks: they are statements about
 * what the data can and cannot support. The rebuild folded them into `tasks`,
 * which lost the distinction — a conflict between two sources is not a job
 * somebody does, it is a reason a number may not be trusted yet.
 *
 * Three families, straight out of the legacy ids:
 *  · `conflict` (`conf-*`) — two sources disagree; do not settle by guessing.
 *  · `limit`    (`limit-*`) — a boundary on what the data covers at all.
 *  · `rule`     (`mail-*`)  — a standing instruction about how a figure may be
 *    read, e.g. case amounts are NOT a receivable balance.
 */
export const DATA_ISSUE_KINDS = ['conflict', 'limit', 'rule'] as const;
export type DataIssueKind = (typeof DATA_ISSUE_KINDS)[number];

export const DATA_ISSUE_CATEGORIES = [
  'unresolved_payment',
  'possible_duplicate_payment',
  'source_amount_difference',
  'contract_support',
  'version_drift',
  'annual_roll_version',
  'estimated_due_dates',
  'unidentified_tenant',
  'identity_conflict',
  'measurement_conflict',
  'data_boundary',
  'marketing',
  'evidence_rule',
  'other',
] as const;
export type DataIssueCategory = (typeof DATA_ISSUE_CATEGORIES)[number];

/**
 * `accepted` is not `resolved`. A conflict that has been looked at and
 * deliberately left standing — because the source genuinely cannot be
 * reconciled — must stay visible next to the number it qualifies.
 */
export const DATA_ISSUE_STATUSES = ['open', 'investigating', 'resolved', 'accepted'] as const;
export type DataIssueStatus = (typeof DATA_ISSUE_STATUSES)[number];

/** Statuses that still qualify a figure. `resolved` is the only one that does not. */
export const QUALIFYING_ISSUE_STATUSES: readonly DataIssueStatus[] = [
  'open',
  'investigating',
  'accepted',
];

// ── Inbox (بريد يحتاج متابعة) ────────────────────────────────────────────────

export const INBOX_PROVIDERS = ['gmail', 'outlook', 'manual', 'other'] as const;
export type InboxProvider = (typeof INBOX_PROVIDERS)[number];

export const INBOX_CATEGORIES = [
  'legal',
  'operations',
  'maintenance',
  'leasing',
  'finance',
  'other',
] as const;
export type InboxCategory = (typeof INBOX_CATEGORIES)[number];

/**
 * Kept at the legacy app's granularity on purpose. "Unread" and "unanswered"
 * are different facts, and one of the migrated data-review rules
 * (`unread_not_unanswered`) exists precisely because conflating them produced
 * a wrong follow-up list. Collapsing these into `open`/`closed` would
 * reintroduce that error.
 */
export const INBOX_REPLY_STATES = [
  'awaiting_external_response',
  'awaiting_external_update',
  'awaiting_external_action',
  'possible_reply_needed',
  'replied_follow_up_completion',
  'replied_waiting_case_update',
  'informational_update',
  'no_action_needed',
  'unknown',
] as const;
export type InboxReplyState = (typeof INBOX_REPLY_STATES)[number];

/** Reply states that put a thread on somebody's desk here, rather than theirs. */
export const INBOX_NEEDS_US: readonly InboxReplyState[] = [
  'possible_reply_needed',
  'awaiting_external_action',
  'unknown',
];

export const INBOX_STATUSES = ['open', 'waiting', 'done', 'archived'] as const;
export type InboxStatus = (typeof INBOX_STATUSES)[number];

/**
 * How a thread came to be linked to a record. `ai` is the only guess — a
 * Workers AI pick from a shortlist of real records — and is flagged for review
 * on the screen; `manual` is a person's decision and is never overwritten.
 */
export const INBOX_MATCH_SOURCES = [
  'address',
  'identifier',
  'attachment',
  'name',
  'ai',
  'manual',
] as const;
export type InboxMatchSource = (typeof INBOX_MATCH_SOURCES)[number];

/** Record types a thread may be linked to, singular — see madar-entity-type-convention. */
export const INBOX_LINK_ENTITY_TYPES = [
  'lease',
  'party',
  'property',
  'unit',
  'charge',
  'payment',
] as const;
export type InboxLinkEntityType = (typeof INBOX_LINK_ENTITY_TYPES)[number];

export const MESSAGE_DIRECTIONS = ['incoming', 'outgoing'] as const;
export type MessageDirection = (typeof MESSAGE_DIRECTIONS)[number];

// ── Calendar, seasons and events (المواسم والفعاليات) ────────────────────────

/**
 * A recurrence is anchored to a CALENDAR. `hijri` is not a formatting choice:
 * عيد الفطر recurs on 1 Shawwal, and its Gregorian date is not knowable in
 * advance — it depends on an official sighting announcement. The legacy data
 * carries `next_date: null` for both Eids for exactly this reason, and that
 * null is preserved rather than being filled with an estimate.
 */
export const RECURRENCE_CALENDARS = ['gregorian', 'hijri', 'none'] as const;
export type RecurrenceCalendar = (typeof RECURRENCE_CALENDARS)[number];

export const EVENT_DATE_CERTAINTY = [
  'verified_official', // published by an official source and checked
  'announced', // announced for this year, not yet an annual rule
  'estimated', // computed; must not drive a commitment
  'unverified', // recurrence known, Gregorian date is not
] as const;
export type EventDateCertainty = (typeof EVENT_DATE_CERTAINTY)[number];

/** Certainty levels a dated commitment may be made against. */
export const COMMITTABLE_CERTAINTY: readonly EventDateCertainty[] = [
  'verified_official',
  'announced',
];

export const EVENT_KINDS = [
  'national',
  'religious',
  'leasing_season',
  'campaign',
  'other',
] as const;
export type EventKind = (typeof EVENT_KINDS)[number];

export const EVENT_PROPOSAL_STATUSES = [
  'none',
  'internal_draft',
  'proposed',
  'approved',
  'cancelled',
] as const;
export type EventProposalStatus = (typeof EVENT_PROPOSAL_STATUSES)[number];

export const EVENT_AUDIENCES = ['owner_only', 'internal', 'tenants', 'public'] as const;
export type EventAudience = (typeof EVENT_AUDIENCES)[number];

// ── Connectors (حالة الاتصال) ────────────────────────────────────────────────

/**
 * Connector health. Every legacy connector is `ready_not_started` or
 * `snapshot_only`: the accounts were read once by hand, nothing is wired up,
 * and no data flows. Naming that state is the point — an operator seeing
 * "Gmail" in a list will otherwise assume mail is being watched.
 */
export const CONNECTOR_HEALTH = [
  'ready_not_started', // credentials proven, nothing scheduled
  'snapshot_only', // a one-off manual read, no sync
  'snapshot_imported', // a snapshot was imported and is awaiting reconciliation
  'live', // syncing
  'degraded',
  'failed',
  'disabled',
] as const;
export type ConnectorHealth = (typeof CONNECTOR_HEALTH)[number];

/** Health values that mean nothing is arriving automatically. */
export const CONNECTOR_NOT_SYNCING: readonly ConnectorHealth[] = [
  'ready_not_started',
  'snapshot_only',
  'snapshot_imported',
  'disabled',
  'failed',
];

// ── Expenses ─────────────────────────────────────────────────────────────────

/**
 * Expense categories. NOI is base rent plus recoveries minus OPEX, so the
 * split that matters is `costClass` (opex vs capex), not this — this is for
 * the operating-statement breakdown a valuer asks for.
 */
export const EXPENSE_CATEGORIES = [
  'cleaning',
  'security',
  'maintenance',
  'lifts',
  'hvac',
  'electricity',
  'water',
  'waste',
  'insurance',
  'municipal_fees',
  'management_fee',
  'marketing',
  'legal',
  'fitout',
  'capital_works',
  /** Rent the group pays as a tenant — staff housing, offices, its cafés. */
  'rent',
  'other',
] as const;
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

export const EXPENSE_STATUSES = ['open', 'approved', 'paid', 'disputed', 'void'] as const;
export type ExpenseStatus = (typeof EXPENSE_STATUSES)[number];

export const COST_CLASSES = ['opex', 'capex', 'non_recoverable', 'financing', 'tax'] as const;
export type CostClass = (typeof COST_CLASSES)[number];

/** Cost classes deducted when computing NOI. Capex and financing are NOT. */
export const NOI_DEDUCTIBLE_CLASSES: readonly CostClass[] = ['opex', 'non_recoverable'];

// ── External staging (مطابقة Odoo) ───────────────────────────────────────────

export const MATCH_STATES = [
  'unmatched',
  'suggested',
  'confirmed',
  'rejected',
  'promoted',
  'conflict',
] as const;
export type MatchState = (typeof MATCH_STATES)[number];
